/**
 * Message Service - Handles messaging functionality in the Superior Payroll application
 * 
 * This service provides functions for sending, retrieving, and managing direct messages
 * and group chats between users.
 */

import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  deleteField,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { UserProfile } from '../contexts/AuthContext';
import { getUserBasicInfo } from './friendService';

// ========= Types =========

// Represents a single message
export interface Message {
  id?: string;
  content: string;
  senderId: string;
  timestamp: any; // Firestore Timestamp
  read: boolean;
  edited?: boolean;
  editedAt?: any; // Firestore Timestamp
  reactions?: {
    [userId: string]: string; // userId: reactionEmoji
  };
  attachments?: {
    type: 'image' | 'file';
    url: string;
    name: string;
    size?: number;
  }[];
}

// Represents a conversation (direct or group)
export interface Conversation {
  id?: string;
  participants: string[]; // Array of user IDs
  type: 'direct' | 'group';
  lastMessage?: {
    content: string;
    senderId: string;
    timestamp: any; // Firestore Timestamp
  };
  name?: string; // For group chats
  createdAt: any; // Firestore Timestamp
  updatedAt: any; // Firestore Timestamp
  unreadCount?: Record<string, number>; // Map of userId to unread count
  otherUser?: {
    uid: string;
    firstName: string;
    lastName: string;
    photoURL?: string;
    department?: string;
    title?: string;
    email?: string;
  }; // For direct conversations, the other user's info
}

// Result interface for operations
interface MessageOperationResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

// ========= Conversations =========

/**
 * Create a new direct conversation between two users
 */
export const createDirectConversation = async (
  userId1: string,
  userId2: string
): Promise<MessageOperationResult> => {
  try {
    // Check if conversation already exists
    const existingConversation = await findDirectConversation(userId1, userId2);

    if (existingConversation.success && existingConversation.data) {
      // Ensure the conversation has otherUser info
      if (!existingConversation.data.otherUser) {
        // Get the other user's info
        const userResult = await getUserBasicInfo(userId2);
        if (userResult.success) {
          existingConversation.data.otherUser = userResult.data;
        }
      }
      
      return {
        success: true,
        message: 'Conversation already exists',
        data: existingConversation.data
      };
    }
    
    // Create new conversation
    // Get the other user's info before creating the conversation
    const userResult = await getUserBasicInfo(userId2);
    
    const conversationData: Omit<Conversation, 'id'> = {
      participants: [userId1, userId2],
      type: 'direct',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      unreadCount: {
        [userId1]: 0,
        [userId2]: 0
      }
    };
    
    // Add otherUser info if available
    if (userResult.success) {
      (conversationData as any).otherUser = userResult.data;
    }
    
    const conversationRef = await addDoc(collection(db, 'conversations'), conversationData);
    
    const newConversation = {
      id: conversationRef.id,
      ...conversationData
    };

    return {
      success: true,
      message: 'Conversation created successfully',
      data: newConversation
    };
  } catch (error: any) {
    console.error('[MessageService] Error creating conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to create conversation'
    };
  }
};

/**
 * Find an existing direct conversation between two users
 */
export const findDirectConversation = async (
  userId1: string,
  userId2: string
): Promise<MessageOperationResult> => {
  try {
    // Look for a direct conversation with exactly these two participants
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('type', '==', 'direct'),
      where('participants', 'array-contains', userId1)
    );
    
    const querySnapshot = await getDocs(conversationsQuery);
    
    let conversation: Conversation | null = null;
    
    querySnapshot.forEach(doc => {
      const data = doc.data() as Conversation;
      if (data.participants.includes(userId2) && data.participants.length === 2) {
        conversation = {
          id: doc.id,
          ...data
        };
      }
    });
    
    if (conversation) {
      // Get the other user's info to include in the conversation
      const userResult = await getUserBasicInfo(userId2);
      if (userResult.success) {
        (conversation as Conversation).otherUser = userResult.data;
      }
      
      return {
        success: true,
        data: conversation
      };
    } else {
      return {
        success: true,
        data: null
      };
    }
  } catch (error: any) {
    console.error('Error finding conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to find conversation'
    };
  }
};

/**
 * Create a new group conversation
 */
export const createGroupConversation = async (
  creatorId: string,
  name: string,
  participantIds: string[]
): Promise<MessageOperationResult> => {
  try {
    // Ensure creator is included in participants
    if (!participantIds.includes(creatorId)) {
      participantIds.push(creatorId);
    }
    
    // Create unread counts for all participants
    const unreadCount: Record<string, number> = {};
    participantIds.forEach(id => {
      unreadCount[id] = 0;
    });
    
    // Create new conversation
    const conversationData: Omit<Conversation, 'id'> = {
      participants: participantIds,
      type: 'group',
      name,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      unreadCount
    };
    
    const conversationRef = await addDoc(collection(db, 'conversations'), conversationData);
    
    return {
      success: true,
      message: 'Group conversation created successfully',
      data: {
        id: conversationRef.id,
        ...conversationData
      }
    };
  } catch (error: any) {
    console.error('Error creating group conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to create group conversation'
    };
  }
};

/**
 * Get all conversations for a user
 */
export const getUserConversations = async (userId: string): Promise<MessageOperationResult> => {
  try {
    // First try with ordering (which requires composite index)
    try {
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(conversationsQuery);
      
      const conversations: Conversation[] = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data() as Conversation;
        conversations.push({
          id: doc.id,
          ...data
        });
      });

      // For direct conversations, get the other user's info
      const conversationsWithDetails = await Promise.all(
        conversations.map(async (conversation) => {
          if (conversation.type === 'direct') {
            const otherUserId = conversation.participants.find(id => id !== userId);
            if (otherUserId) {
              // Always fetch the latest user info to ensure we have the most current photoURL
              const userResult = await getUserBasicInfo(otherUserId);
              if (userResult.success) {
                return {
                  ...conversation,
                  otherUser: userResult.data
                };
              } else {
                console.error(`[MessageService] Failed to get user info for: ${otherUserId}`);
              }
            }
          }
          return conversation;
        })
      );
      
      return {
        success: true,
        data: conversationsWithDetails
      };
    } catch (indexError: any) {
      // If we get an index error, fall back to a simpler query without ordering
      if (indexError.message && (
          indexError.message.includes('requires an index') || 
          indexError.message.includes('no matching index')
        )) {
        console.warn(`[MessageService] Index error, falling back to simple query: ${indexError.message}`);
        
        // Fallback to simple query without ordering
        const simpleQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', userId)
        );
        
        const simpleSnapshot = await getDocs(simpleQuery);
        
        const conversations: Conversation[] = [];
        
        simpleSnapshot.forEach(doc => {
          const data = doc.data() as Conversation;
          conversations.push({
            id: doc.id,
            ...data
          });
        });

        // Sort the conversations manually by updatedAt (if available)
        const sortedConversations = [...conversations].sort((a, b) => {
          const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return timeB - timeA; // descending order
        });
        
        // For direct conversations, get the other user's info
        const conversationsWithDetails = await Promise.all(
          sortedConversations.map(async (conversation) => {
            if (conversation.type === 'direct') {
              const otherUserId = conversation.participants.find(id => id !== userId);
              if (otherUserId) {
                // Always fetch the latest user info to ensure we have the most current photoURL
                const userResult = await getUserBasicInfo(otherUserId);
                if (userResult.success) {
                  return {
                    ...conversation,
                    otherUser: userResult.data
                  };
                } else {
                  console.error(`[MessageService] Failed to get user info for: ${otherUserId}`);
                }
              }
            }
            return conversation;
          })
        );
        
        return {
          success: true,
          data: conversationsWithDetails
        };
      } else {
        // If it's not an index error, rethrow
        throw indexError;
      }
    }
  } catch (error: any) {
    console.error('[MessageService] Error getting user conversations:', error);
    return {
      success: false,
      error: error.message || 'Failed to get user conversations'
    };
  }
};

/**
 * Add user to group conversation
 */
export const addUserToGroupConversation = async (
  conversationId: string,
  userId: string
): Promise<MessageOperationResult> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      return {
        success: false,
        error: 'Conversation not found'
      };
    }
    
    const conversationData = conversationDoc.data() as Conversation;
    
    if (conversationData.type !== 'group') {
      return {
        success: false,
        error: 'Not a group conversation'
      };
    }
    
    if (conversationData.participants.includes(userId)) {
      return {
        success: false,
        error: 'User is already in this conversation'
      };
    }
    
    // Add user to participants and set their unread count to 0
    await updateDoc(conversationRef, {
      participants: arrayUnion(userId),
      [`unreadCount.${userId}`]: 0
    });
    
    return {
      success: true,
      message: 'User added to conversation'
    };
  } catch (error: any) {
    console.error('Error adding user to conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to add user to conversation'
    };
  }
};

/**
 * Remove user from group conversation
 */
export const removeUserFromGroupConversation = async (
  conversationId: string,
  userId: string
): Promise<MessageOperationResult> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      return {
        success: false,
        error: 'Conversation not found'
      };
    }
    
    const conversationData = conversationDoc.data() as Conversation;
    
    if (conversationData.type !== 'group') {
      return {
        success: false,
        error: 'Not a group conversation'
      };
    }
    
    if (!conversationData.participants.includes(userId)) {
      return {
        success: false,
        error: 'User is not in this conversation'
      };
    }

    // Create a new object to update Firestore
    const updates: Record<string, any> = {
      participants: conversationData.participants.filter(id => id !== userId)
    };
    
    // If we have unreadCount, create a new object without the user being removed
    if (conversationData.unreadCount) {
      const newUnreadCount: Record<string, number> = {};
      Object.keys(conversationData.unreadCount).forEach(id => {
        if (id !== userId) {
          newUnreadCount[id] = conversationData.unreadCount![id];
        }
      });
      updates.unreadCount = newUnreadCount;
    }
    
    await updateDoc(conversationRef, updates);
    
    return {
      success: true,
      message: 'User removed from conversation'
    };
  } catch (error: any) {
    console.error('Error removing user from conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove user from conversation'
    };
  }
};

/**
 * Rename group conversation
 */
export const renameGroupConversation = async (
  conversationId: string,
  newName: string
): Promise<MessageOperationResult> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      return {
        success: false,
        error: 'Conversation not found'
      };
    }
    
    const conversationData = conversationDoc.data() as Conversation;
    
    if (conversationData.type !== 'group') {
      return {
        success: false,
        error: 'Not a group conversation'
      };
    }
    
    await updateDoc(conversationRef, {
      name: newName
    });
    
    return {
      success: true,
      message: 'Conversation renamed successfully'
    };
  } catch (error: any) {
    console.error('Error renaming conversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to rename conversation'
    };
  }
};

// ========= Messages =========

/**
 * Send a message in a conversation
 */
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  content: string,
  attachments?: { type: 'image' | 'file'; url: string; name: string; size?: number }[]
): Promise<MessageOperationResult> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      return {
        success: false,
        error: 'Conversation not found'
      };
    }
    
    const conversationData = conversationDoc.data() as Conversation;
    
    if (!conversationData.participants.includes(senderId)) {
      return {
        success: false,
        error: 'User is not a participant in this conversation'
      };
    }
    
    // Create message - only include attachments if they're provided and valid
    const messageData: Omit<Message, 'id'> = {
      content,
      senderId,
      timestamp: serverTimestamp(),
      read: false
    };
    
    // Only add attachments field if it's a non-empty array
    if (attachments && Array.isArray(attachments) && attachments.length > 0) {
      messageData.attachments = attachments;
    }
    
    // Add message to messages collection
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const messageRef = await addDoc(messagesRef, messageData);
    
    // Update conversation with last message
    const lastMessage = {
      content,
      senderId,
      timestamp: serverTimestamp()
    };
    
    // Update unread counts for all participants except sender
    const unreadCountUpdates: Record<string, number> = {};
    
    conversationData.participants.forEach(userId => {
      if (userId !== senderId) {
        const currentCount = conversationData.unreadCount?.[userId] || 0;
        unreadCountUpdates[`unreadCount.${userId}`] = currentCount + 1;
      }
    });
    
    await updateDoc(conversationRef, {
      lastMessage,
      updatedAt: serverTimestamp(),
      ...unreadCountUpdates
    });
    
    return {
      success: true,
      message: 'Message sent successfully',
      data: {
        id: messageRef.id,
        ...messageData
      }
    };
  } catch (error: any) {
    console.error('Error sending message:', error);
    return {
      success: false,
      error: error.message || 'Failed to send message'
    };
  }
};

/**
 * Get messages from a conversation
 */
export const getConversationMessages = async (
  conversationId: string,
  limitCount: number = 50
): Promise<MessageOperationResult> => {
  try {
    const messagesQuery = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(messagesQuery);
    
    const messages: Message[] = [];
    
    querySnapshot.forEach(doc => {
      const data = doc.data() as Message;
      messages.push({
        id: doc.id,
        ...data
      });
    });
    
    return {
      success: true,
      data: messages.reverse() // Return in chronological order
    };
  } catch (error: any) {
    console.error('Error getting conversation messages:', error);
    return {
      success: false,
      error: error.message || 'Failed to get conversation messages'
    };
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (
  conversationId: string,
  userId: string
): Promise<MessageOperationResult> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    
    // Reset unread count for user
    await updateDoc(conversationRef, {
      [`unreadCount.${userId}`]: 0
    });
    
    return {
      success: true,
      message: 'Messages marked as read'
    };
  } catch (error: any) {
    console.error('Error marking messages as read:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark messages as read'
    };
  }
};

/**
 * Delete a message
 */
export const deleteMessage = async (
  conversationId: string,
  messageId: string
): Promise<MessageOperationResult> => {
  try {
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    await deleteDoc(messageRef);
    
    return {
      success: true,
      message: 'Message deleted successfully'
    };
  } catch (error: any) {
    console.error('Error deleting message:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete message'
    };
  }
};

/**
 * Set up a real-time listener for new messages in a conversation
 * Returns a function to unsubscribe from the listener
 */
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void
): (() => void) => {
  try {
    const messagesQuery = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribe = onSnapshot(
      messagesQuery, 
      (snapshot) => {
        try {
          const messages: Message[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data() as Message;
            messages.push({
              id: doc.id,
              ...data
            });
          });
          callback(messages);
        } catch (error) {
          console.error(`[MessageService] Error processing message snapshot: ${error}`);
        }
      },
      (error) => {
        console.error(`[MessageService] Error in message subscription: ${error}`);
      }
    );
    
    return () => {
      unsubscribe();
    };
  } catch (error) {
    console.error(`[MessageService] Failed to set up message subscription: ${error}`);
    // Return a no-op function so the app doesn't crash
    return () => {}; 
  }
};

/**
 * Subscribe to updates for user's conversations
 */
export const subscribeToConversations = (
  userId: string, 
  onUpdate: (conversations: Conversation[]) => void
): (() => void) => {
  try {
    // First try with ordering (which requires composite index)
    try {
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );
      
      return onSnapshot(conversationsQuery, async (snapshot) => {
        try {
          const conversations: Conversation[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data() as Conversation
          }));
          
          // For direct conversations, get the other user's info
          const conversationsWithDetails = await Promise.all(
            conversations.map(async (conversation) => {
              if (conversation.type === 'direct') {
                const otherUserId = conversation.participants.find(id => id !== userId);
                if (otherUserId) {
                  // Always fetch the latest user info to ensure we have the most current photoURL
                  const userResult = await getUserBasicInfo(otherUserId);
                  if (userResult.success) {
                    return {
                      ...conversation,
                      otherUser: userResult.data
                    };
                  }
                }
              }
              return conversation;
            })
          );
          
          onUpdate(conversationsWithDetails);
        } catch (error) {
          console.error('[MessageService] Error processing conversation snapshot:', error);
        }
      }, (error) => {
        console.error('[MessageService] Error in conversation subscription:', error);
      });
    } catch (indexError: any) {
      // If we get an index error, fall back to a simpler query without ordering
      if (indexError.message && (
          indexError.message.includes('requires an index') || 
          indexError.message.includes('no matching index')
        )) {
        console.warn(`[MessageService] Index error, falling back to simple query: ${indexError.message}`);
        
        // Fallback to simple query without ordering
        const simpleQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', userId)
        );
        
        return onSnapshot(simpleQuery, async (snapshot) => {
          try {
            let conversations: Conversation[] = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data() as Conversation
            }));
            
            // Sort the conversations manually by updatedAt (if available)
            conversations = conversations.sort((a, b) => {
              const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
              const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
              return timeB - timeA; // descending order
            });
            
            // For direct conversations, get the other user's info
            const conversationsWithDetails = await Promise.all(
              conversations.map(async (conversation) => {
                if (conversation.type === 'direct') {
                  const otherUserId = conversation.participants.find(id => id !== userId);
                  if (otherUserId) {
                    // Always fetch the latest user info to ensure we have the most current photoURL
                    const userResult = await getUserBasicInfo(otherUserId);
                    if (userResult.success) {
                      return {
                        ...conversation,
                        otherUser: userResult.data
                      };
                    }
                  }
                }
                return conversation;
              })
            );
            
            onUpdate(conversationsWithDetails);
          } catch (error) {
            console.error('[MessageService] Error processing conversation snapshot (fallback):', error);
          }
        }, (error) => {
          console.error('[MessageService] Error in conversation subscription (fallback):', error);
        });
      } else {
        // This is not an index error, rethrow
        throw indexError;
      }
    }
  } catch (error) {
    console.error('[MessageService] Error setting up conversation subscription:', error);
    return () => {}; // Return a no-op function in case of error
  }
};

/**
 * Get the total unread message count across all conversations for a user
 */
export const getTotalUnreadCount = async (userId: string): Promise<MessageOperationResult> => {
  try {
    // Query all conversations where the user is a participant
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId)
    );
    
    const querySnapshot = await getDocs(conversationsQuery);
    
    // Loop through conversations and sum up unread counts
    let totalUnread = 0;
    
    querySnapshot.forEach(docSnapshot => {
      const data = docSnapshot.data() as Conversation;
      // Check if this conversation has unread messages for this user
      if (data.unreadCount && typeof data.unreadCount[userId] === 'number') {
        totalUnread += data.unreadCount[userId];
      }
    });
    
    return {
      success: true,
      data: totalUnread
    };
  } catch (error: any) {
    console.error('Error getting total unread count:', error);
    return {
      success: false,
      error: error.message || 'Failed to get total unread count'
    };
  }
};

/**
 * Add or update reaction to a message
 */
export const addReactionToMessage = async (
  conversationId: string,
  messageId: string,
  userId: string,
  reaction: string
): Promise<MessageOperationResult> => {
  try {
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    const messageDoc = await getDoc(messageRef);
    
    if (!messageDoc.exists()) {
      return {
        success: false,
        error: 'Message not found'
      };
    }
    
    // Update the reactions field
    await updateDoc(messageRef, {
      [`reactions.${userId}`]: reaction
    });
    
    return {
      success: true,
      message: 'Reaction added successfully'
    };
  } catch (error: any) {
    console.error('Error adding reaction to message:', error);
    return {
      success: false,
      error: error.message || 'Failed to add reaction to message'
    };
  }
};

/**
 * Remove a reaction from a message
 */
export const removeReactionFromMessage = async (
  conversationId: string,
  messageId: string,
  userId: string
): Promise<MessageOperationResult> => {
  try {
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    const messageDoc = await getDoc(messageRef);

    if (!messageDoc.exists()) {
      return {
        success: false,
        error: 'Message not found'
      };
    }

    // Create FieldValue to remove the specific user's reaction
    await updateDoc(messageRef, {
      [`reactions.${userId}`]: deleteField()
    });
    
    return {
      success: true,
      message: 'Reaction removed successfully'
    };
  } catch (error: any) {
    console.error('Error removing reaction from message:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove reaction from message'
    };
  }
};

/**
 * Edit a message
 */
export const editMessage = async (
  conversationId: string,
  messageId: string,
  userId: string,
  newContent: string
): Promise<MessageOperationResult> => {
  try {
    const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
    const messageDoc = await getDoc(messageRef);
    
    if (!messageDoc.exists()) {
      return {
        success: false,
        error: 'Message not found'
      };
    }
    
    const messageData = messageDoc.data() as Message;
    
    // Ensure only the original sender can edit the message
    if (messageData.senderId !== userId) {
      return {
        success: false,
        error: 'Only the original sender can edit this message'
      };
    }
    
    // Update the message content
    await updateDoc(messageRef, {
      content: newContent,
      edited: true,
      editedAt: serverTimestamp()
    });
    
    // If this was the last message in the conversation, update that too
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data() as Conversation;
      
      if (conversationData.lastMessage && 
          messageData.senderId === conversationData.lastMessage.senderId && 
          messageData.timestamp === conversationData.lastMessage.timestamp) {
        await updateDoc(conversationRef, {
          'lastMessage.content': newContent
        });
      }
    }
    
    return {
      success: true,
      message: 'Message edited successfully',
      data: {
        ...messageData,
        content: newContent,
        edited: true
      }
    };
  } catch (error: any) {
    console.error('Error editing message:', error);
    return {
      success: false,
      error: error.message || 'Failed to edit message'
    };
  }
}; 